/* Single source of truth for the API origin and shared request headers.
   Every fetch in the app should build its URL via `apiUrl()` (or the `api`
   client in api.ts which delegates here) so VITE_API_URL is honoured
   consistently — no relative "/api/..." URLs that silently hit the Vite
   dev-server origin instead of Django. */

export const DEFAULT_API_BASE = "http://localhost:8000"

/** Trim whitespace + trailing slashes; fall back when unset/empty. */
export function normalizeApiBase(
  raw: string | undefined | null,
  fallback: string = DEFAULT_API_BASE,
): string {
  const value = (raw ?? "").trim()
  if (!value) return fallback
  return value.replace(/\/+$/, "")
}

/** Join a base origin and a path, tolerating a missing leading slash. */
export function joinApiUrl(base: string, path: string): string {
  return `${base}${path.startsWith("/") ? path : `/${path}`}`
}

/** Build request headers, attaching X-API-Key when an api key is configured. */
export function buildApiHeaders(
  apiKey: string | null,
  extra?: Record<string, string>,
): Record<string, string> {
  const headers: Record<string, string> = { ...(extra ?? {}) }
  if (apiKey) headers["X-API-Key"] = apiKey
  return headers
}

/* ---- Environment-bound singletons ---- */

export const API_BASE = normalizeApiBase(
  import.meta.env["VITE_API_URL"] as string | undefined,
)

/** Optional API key — surfaced as X-API-Key on every API call when set. */
export const API_KEY: string | null =
  ((import.meta.env["VITE_API_KEY"] as string | undefined) ?? "").trim() || null

/** Absolute URL on the configured API origin. */
export function apiUrl(path: string): string {
  return joinApiUrl(API_BASE, path)
}

/** Headers for API calls (X-API-Key included when configured). */
export function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  return buildApiHeaders(API_KEY, extra)
}
